/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import { types, getEnv } from 'mobx-state-tree';
import * as mobx from 'mobx';
import _ from 'underscore';
import ExploreToolbarModel from './ExploreToolbarModel';
import CardActions from 'components/cards/CardActions';
import TabsModel from 'components/tabs/TabsModel';
import RecommendedModel from 'lib/@waca/vis-recommender/src/js/RecommendedModel';
import CardNotificationViewModel from 'glass/notification/CardNotificationViewModel';
import SelectionModel from './SelectionModel';
import SelectionSyncModel from './SelectionSyncModel';
import BackgroundThumbnailsModel from './thumbnail/BackgroundThumbnailsModel';
import ExploreVMUtils from './ExploreVMUtils';
import { fromPromise } from 'mobx-utils';
import { performContentSelection } from 'util/SelectionUtil';
//import { fromPromise } from '@waca/thenable-state/dist/ThenableState';
import Logger from 'util/Logger';
import {
	CompareCardSmartsRecommendations,
	CompareCardCustomRecommendations
} from 'features/compare/recommendations';
export const STARTING_POINTS = 'starting points';

import { isAlive } from 'mobx-state-tree';

const SMALL_REACTION_DELAY = 200;

export const FEATURES = {
	CANVAS: {
		FILTER_DOCK: 'filterDock'
	}
};

export default types
	.model('ExploreViewModel', {
		displayedCard: types.optional(types.string, STARTING_POINTS),
		indexOfDisplayedCard: types.optional(types.number, -1),
		activeDataSourceId: types.maybeNull(types.string),
		showModalVisTypePicker: types.optional(types.boolean, false),
		showModalRelatedVisPicker: types.optional(types.boolean, false),
		showCompareSlideout: types.optional(types.boolean, false),
		showCanvasSidePanel: types.optional(types.boolean, true),
		toolbarVisPicker: types.optional(types.boolean, true),
		toolbar: ExploreToolbarModel,
		canvasTabs: TabsModel,
		notification: CardNotificationViewModel,
		thumbnails: BackgroundThumbnailsModel,
		selectedCards: SelectionModel,
		selectionSync: SelectionSyncModel
	})
	.volatile(() => ({
		_isAlive: isAlive,
		exploreVMUtils: ExploreVMUtils,
		activeDataSourceModule: null,
		cardActions: null,
		widgetInstance: null,
		layoutController: null,
		boardModel: null,
		layoutModel: null,
		exploreContainerNode: null,
		visTypes: null,
		thumbnailSvc: null,
		_disposeEvents: [],
		_mobxDisposeEvents : [],
		widgetSnapshotForAlternative: null,
		widgetSnapshotForRelated: null,
		alternativeRecommendationVM: null,
		relatedRecommendationVM: null,
		compareCardSmartsRecommendationVM: null,
		itemInCardToSelectWhenVisible: null,
		logger: null
	}))
	.extend((self) => {
		function _getLayout(id) {
			const canvas = getEnv(self).dashboardApi.getCanvas();

			return canvas && canvas.getLayout(id);
		}

		function _getSelectedWidgets() {
			const canvas = getEnv(self).dashboardApi.getCanvas();

			return canvas ? canvas.getSelectedWidgets() : [];
		}

		function _getWidget(id) {
			return getEnv(self).dashboardApi.getCanvasWhenReady()
				.then(canvas => canvas.getWidgetWhenReady(id));
		}

		function setupNLTReactions() {
			self._mobxDisposeEvents.push(
				mobx.reaction(() => {
					return self.selectedWidget;
				}, () => {
					self.fetchNLT();
				}, { delay: SMALL_REACTION_DELAY }),
				mobx.reaction(() => {
					return self.canvasTabs.getTab();
				}, () => {
					self.fetchNLT();
				}, { delay: SMALL_REACTION_DELAY }),
				mobx.reaction(() => {
					return self.canShowCanvasSidePanel();
				}, () => {
					self.fetchNLT();
				}, { delay: SMALL_REACTION_DELAY })
			);
		}

		function setupToolbarReactions() {
			self._mobxDisposeEvents.push(
				mobx.reaction(() => {
					return self.displayedCard;
				}, card => {
					if (card === STARTING_POINTS) {
						self.toolbar.resetActions();
					} else {
						// A new card is being displayed; fetch the toolbar items.
						self.toolbar.fetchWidgetActions();
					}
					self.fetchNLT();
				}, { delay: SMALL_REACTION_DELAY })
			);
		}

		function setupDataSourceModule() {
			self._mobxDisposeEvents.push(
				mobx.reaction(
					() => self.activeDataSourceId,
					activeDataSourceId => {
						if(activeDataSourceId) {
							self._setActiveDataSourceModule();
						}
					}
				),
			);
		}

		function _getDataSourceOfSelectedCard(cardId) {
			const widgetIds = self.boardModel.layout.listWidgets([cardId]);
			if (widgetIds && widgetIds.length) {
				// single or compare card, active data source is the data source of the first widget
				const content = getContentFromWidget(widgetIds[0]);
				const visualization = content.getFeature('Visualization');
				const dataSource = visualization && visualization.getDataSource();
				if (dataSource) {
					return dataSource.getId();
				}
			}
			return null;
		}

		function getCanvasFeature(feature) {
			return getEnv(self).dashboardApi.getCanvas().getFeature(feature);
		}

		// Visited cards need to be flagged as current if they are visited
		// otherwise an empty thumbnail will be rendered.
		// Visited cards start the rendering and they need to be visible
		// for when the generation of the thumbnail takes place.
		function forceAddCurrentThumbnail(id) {
			const thumbnailsModel = self.thumbnails;
			const containsBlankWidget = self.containsBlankWidget(id);
			if (!thumbnailsModel.isCurrent(id) && !thumbnailsModel.isDone(id) && !containsBlankWidget) {
				thumbnailsModel.addToCurrent(id);
			}
		}

		function getVisTypeFromId(id) {
			return getEnv(self).dashboardApi
				.getFeature('VisDefinitions')
				.getById(id)
				.getType();
		}

		function getContentFromWidget(widgetId) {
			const canvas = getEnv(self).dashboardApi.getCanvas();
			const content = canvas.getContent(widgetId);
			if (!content) {
				return;
			}
			return content;
		}

		function setContentVisType(content, visType) {
			const recommender = content.getFeature('Visualization.SmartsRecommender');
			if (recommender) {
				recommender.setType(visType);
			}
		}

		function setContentVisTypeFixed(content, visId) {
			const visType = getVisTypeFromId(visId);
			if (!visType) {
				return;
			}
			setContentVisType(content, visType);
		}

		function setContentVisTypeAuto(content) {
			setContentVisType(content, 'auto');
		}

		return {
			views: {
				isStartingPointsDisplayed() {
					return self.displayedCard === STARTING_POINTS;
				},
				getDisplayedCard() {
					if(self.isStartingPointsDisplayed()) {
						throw new Error('No card selected');
					}
					return self.displayedCard;

				},
				isPlaceholderWidget(id) {
					const model = self.getWidgetModel(id);

					return !!(model && model.type === 'placeholder');
				},

				getContentForSelectedWidget() {
					const canvas = self.getDashboardApi().getCanvas();
					if (!this.selectedWidget) {
						return false;
					}
					const widgetId = this.selectedWidget.getId();
					if (!widgetId) {
						return false;
					}
					const content = canvas.getContent(widgetId);
					if (!content) {
						return false;
					}
					return content;
				},

				getVisTypeFromId(id) {
					const visDefinition = self.getDashboardApi().getFeature('VisDefinitions').getById(id);
					return visDefinition && visDefinition.getType();
				},

				isSelectedWidgetMappingComplete() {
					const content = self.getContentForSelectedWidget();
					const visFeature = content && content.getFeature('Visualization');
					return visFeature && visFeature.getSlots().isMappingComplete();
				},

				getSmartsRecommenderForContent(content) {
					const smartsRecommenderApi = content && content.getFeature('Visualization.SmartsRecommender');
					return smartsRecommenderApi;
				},

				getDashboardApi() {
					return getEnv(self).dashboardApi;
				},

				/**
				 * Returns false if the card has at least 1 valid widget (ie. not a placeholder widget). Otherwise returns true.
				 *
				 * @return {boolean} whether the current displayed card is empty
				 */
				isEmptyCard() {
					if (self.isStartingPointsDisplayed()) {
						return true;
					}
					const layoutAPI = _getLayout(self.displayedCard);
					if (layoutAPI) {
						// A card is not empty, if it contains at least one valid (non-placeholder) widget.
						const foundValidWidget = layoutAPI.getChildren()
							.some(item => !self.isPlaceholderWidget(item.getId()));

						return !foundValidWidget;
					}
					return true;
				},
				setFocusOnExploreContainer(){
					self.exploreContainerNode.focus();
				},
				isCompare() {
					const id = self.boardModel.getSelectedLayout();
					const layout = self.boardModel.layout.findModel(id);

					return !!(layout && layout.isCompareEnabled);
				},
				/*Only show related viz toolbar button if there is only one selected widget that has data*/
				isRelatedVizToolbarItemEnabled() {
					return (self.selectedWidgets && self.selectedWidgets.length === 1 && self.hasDataInActiveWidgets());
				},
				hasDataInActiveWidgets() {
					if(self.isStartingPointsDisplayed()) {
						return false;
					}
					const cardId = self.getDisplayedCard();
					if (!cardId) {
						return false;
					}
					const widgetIds = self.boardModel.layout.listWidgets([cardId]);
					if (!widgetIds || widgetIds.length === 0) {
						return false;
					}

					// TODO [livewidget_cleanup] content.getFeature('Visualization') is not ready now, - need refactor
					return widgetIds.some((widgetId) => {
						const widget = self.getWidgetModel(widgetId);
						if (!widget || !widget.data || !widget.data.dataViews) {
							return false;
						}
						return widget.data.dataViews.some((dataView) => {
							if (!dataView.dataItems) {
								return false;
							}
							return dataView.dataItems.length > 0;
						});
					});
				},
				isCardDisplayed(id) {
					return !self.isStartingPointsDisplayed() && self.displayedCard === id;
				},
				getSmartsRecommenderWrapper() {
					return getEnv(self).smartsRecommenderWrapper;
				},
				getWidgetModel(id) {
					return self.boardModel.getWidgetModel(id);
				},
				canNavigatePrevious() {
					if (self.isStartingPointsDisplayed()) {
						const items = self.layoutModel.items;
						return !!(items && items.length);
					}

					const currentItem = self.layoutModel.findModel(self.displayedCard);
					if (currentItem) {
						return !!currentItem.getPreviousSiblingId();
					}
					return false;
				},
				canNavigateNext() {
					return !self.isStartingPointsDisplayed();
				},
				canShowCanvasSidePanel() {
					return self.showCanvasSidePanel && !self.isStartingPointsDisplayed();
				},
				/**
				 * Check whether the card with a specific id contains a blank widget or not
				 * @param {string} cardId - card id
				 * @return {boolean} TRUE if the card contains a blank widget and FALSE if not
				 */
				containsBlankWidget(cardId) {
					const card = self.layoutModel.findModel(cardId);
					let result = false;
					if (card) {
						result = (card.items || [])
							.some(item => self.isPlaceholderWidget(item.id));
					}
					return result;
				},
				get selectedWidgets() {
					const model = self.layoutModel && self.layoutModel.findModel(self.displayedCard);

					void(model && model.selected);

					self.layoutModel && self.layoutModel.items
						.forEach(item => item.selected);

					return _getSelectedWidgets();
				},
				get selectedWidget() {
					const widgets = self.selectedWidgets;
					if (widgets.length === 1) {
						return widgets[0];
					}
					return null;
				},
				get selectedWidgetModel() {
					const selectedWidget = self.selectedWidget;

					return selectedWidget && self.getWidgetModel(selectedWidget.getId());
				},
				get selectedWidgetLayoutModel() {
					const selectedWidget = self.selectedWidget;

					return selectedWidget && self.layoutModel.findModel(selectedWidget.getId());
				},
				get numberOfCards() {
					const {
						layoutModel: {
							items
						} = {}
					} = self;

					return items ? items.length : 0;
				},
				get displayedCardIndex() {
					let index = -1;

					const displayedCard = self.displayedCard;
					if (displayedCard) {
						let count = 0;
						self.layoutModel.items.some(item => {
							const found = item.id === displayedCard;
							if (found) {
								index = count;
							} else {
								count++;
							}
							return found;
						});
					}
					return index;
				},
				isCardNew(cardId) {
					const card = self.layoutModel.findModel(cardId);
					if (card) {
						return card.isNew;
					}
				},

				/* Smarts recommendations */

				/**
				 * Get compare card recommendations
				 *
				 * @param {object} widgetWrapper - widget wrapper
				 * @param {WidgetAPI} widgetWrapper.widgetApi - widget api
				 * @param {WidgetModel} widgetWrapper.widgetModel - widget model
				 */
				getCompareCardRecommendations(widgetWrapper) {
					return self.compareCardSmartsRecommendationVM.fetchRecommendations(widgetWrapper)
						.then(() => {
							return self.compareCardSmartsRecommendationVM.getRecommendations();
						});
				},

				/* feature flags */

				/* feature flag for new starting points layout and vis preview*/
				isNewVisLayoutEnabled() {
					return !getEnv(self).isFeatureEnabled('newStartingPoints', 'false');
				},

				/* feature flag for new starting points layout and vis preview*/
				isFilterStartingPointsEnabled() {
					return getEnv(self).isFeatureEnabled('filterStartingPoints');
				},

				/* feature flag for new compare card recommendations */
				isNewCompareCardRecommendationsEnabled() {
					return !getEnv(self).isFeatureEnabled('newCompareCardRecommendations', 'false');
				},

				/*
					feature flag for new compare card recommendations - powered by smarts.
						true -> request and use smarts recommendations
						false -> use client side rules
				*/
				isNewCompareCardSmartsRecommendationsEnabled() {
					return !getEnv(self).isFeatureEnabled('newCompareCardRecommendationsSmarts','false');
				}
			},
			actions: {
				afterCreate() {
					setupToolbarReactions();
					setupNLTReactions();
					setupDataSourceModule();

					self.selectionSync.setParentModel(self);
					self.setLogger(new Logger('ExploreViewModel'));

					self.alternativeRecommendationVM = new RecommendedModel();
					self.alternativeRecommendationVM.setRecommender(self.getSmartsRecommenderWrapper());
					self.alternativeRecommendationVM.setNewLayoutEnabled(self.isNewVisLayoutEnabled());

					self.relatedRecommendationVM = new RecommendedModel();
					self.relatedRecommendationVM.setRecommender(self.getSmartsRecommenderWrapper());
					self.relatedRecommendationVM.setNewLayoutEnabled(self.isNewVisLayoutEnabled());

					/* Compare card slideout recommendation view models */

					/* non-smarts simple recommendations */

					self.compareCardCustomRecommendationVM = new RecommendedModel();
					self.compareCardCustomRecommendationVM.setRecommender(
						new CompareCardCustomRecommendations({
							dashboardApi: getEnv(self).dashboardApi,
							isFeatureEnabled: getEnv(self).isFeatureEnabled
						})
					);

					/* smarts recommendations */

					self.compareCardSmartsRecommendationVM = new RecommendedModel();
					self.compareCardSmartsRecommendationVM.setRecommender(
						new CompareCardSmartsRecommendations({
							dashboardApi: getEnv(self).dashboardApi,
							isSmartsEnabled: self.isNewCompareCardSmartsRecommendationsEnabled
						})
					);
				},
				beforeDestroy() {
					// Remove the events.
					self._disposeEvents.forEach(dispose => dispose.remove());
					self._mobxDisposeEvents.forEach(dispose => dispose());
					self.widgetInstance = null;
				},
				setModelChecker( isAlive ) {
					self._isAlive = isAlive;
				},
				setExploreContainerNode(ref) {
					self.exploreContainerNode = ref;
				},
				setLogger(logger) {
					self.logger = logger;
				},
				notifyTabChanged(id) {
					// Notify that the card has changed. The contextual grid and filter dock will listen to this.
					getEnv(self).eventRouter.trigger('tab:tabChanged', { modelId: id });
				},
				fetchNLT() {
					if( !isAlive(self) ) {
						return;
					}
					if (!self.canShowCanvasSidePanel()) {
						return;
					} else if (self.canvasTabs.getTab() !== 'details') {
						return;
					}

					let widget;
					const widgets = _getSelectedWidgets();
					if (widgets.length === 1) {
						widget = widgets[0];
					}

					if (widget) {
						const widgetLayout = self.layoutModel.findModel(widget.getId());

						widgetLayout.nlt.fetchItems();
					}
				},
				activateCanvasFeature(feature) {
					return getCanvasFeature(feature)
						.then((featureApi) => {
							if (featureApi) {
								return featureApi.activate();
							} else {
								throw new Error(`Non-existing feature: ${feature}`);
							}
						});
				},
				deactivateCanvasFeature(feature) {
					return getCanvasFeature(feature)
						.then((featureApi) => {
							if (featureApi) {
								return featureApi.deactivate();
							} else {
								return Promise.reject(new Error(`Non-existing feature: ${feature}`));
							}
						});
				},
				selectWidget(id) {
					const dashboardApi = getEnv(self).dashboardApi;
					if (dashboardApi) {
						return dashboardApi.getCanvasWhenReady().then((canvas)=> {
							const state = canvas.getContent(id).getFeature('state');
							const currentState = state.getStatus();

							if(currentState === state.STATUS.CREATED || currentState === state.STATUS.INITIALIZED) {
								// content has four states: CREATED, INITIALIZED, RENDERING, RENDERED
								// if the content is in `CREATED` or `INITIALIZED` state, then we need to wait
								// the content to at least start rendering before we can select.
								return state.whenStatusChanges(state.STATUS.RENDERING).then(() => {
									performContentSelection(dashboardApi, [id]);
								});
							} else {
								// otherwise the content must already be rendering or already rendered 
								// so that we can immediately select the content.
								performContentSelection(dashboardApi, [id]);
							}
							return Promise.resolve(true);
						});

					}
					return Promise.resolve(true);
				},

				setItemInCardToSelectWhenVisible(item){
					self.itemInCardToSelectWhenVisible = item;
				},
				selectItemInCardWhenVisible( ){
					if( !isAlive(self) ) {
						return Promise.resolve();
					}
					if (self.itemInCardToSelectWhenVisible){
						const { widgetId, tabId } = self.itemInCardToSelectWhenVisible;
						return self.selectWidget(widgetId).then( () => {
							if ( self.canShowCanvasSidePanel() && self.canvasTabs.getTab() !== tabId){
								self.canvasTabs.showTab(tabId);
							}
							self.setItemInCardToSelectWhenVisible(null);
						});
					}
					return Promise.resolve();
				},
				syncCardSelection() {
					if(self.isStartingPointsDisplayed()) {
						self.selectedCards.clear();
					} else {
						self.selectedCards.select(self.getDisplayedCard());
					}
				},
				syncWidgetSelection() {
					const displayedCard = self.displayedCard;
					self.layoutController.getLayoutViewWhenReady(displayedCard)
						.then((layoutView) => {
							if (layoutView) {
								const card = self.layoutModel.findModel(displayedCard);

								if (card && card.items.length === 1) {
									const widget = card.items[0];
									widget && self.selectWidget(widget.id);
								}
							}
						});
				},
				deselectAllWidgets() {
					const dashboardApi = getEnv(self).dashboardApi;
					const canvasApi = dashboardApi && dashboardApi.getCanvas();
					if (canvasApi) {
						const selectedContentList = canvasApi.getSelectedContentList();
						const selectedContentIds = selectedContentList.map(item => item.getId());
						canvasApi.deselectContent(selectedContentIds);
					}
				},

				/**
					* Change the vis type either by doing recommendation and manual choose
					* @param {object} recommendation - recommended viz
					* @param {boolean} isSetByAuto - automatically recommend or manually choose
					* @return {object} a promise which would be resolved when the action is done
					*/
				changeVisType(recommendation, isSetByAuto){
					// TODO: refactor this.
					// observation - in the case where isSetByAuto is true, we don't don't read the recommendation argument at all. is that expected?
					// consider having 2 methods:
					// - one to change vis type based on a recommendation
					// - one to change vis type based on auto
					let oRecommendation = recommendation;
					if(isSetByAuto){
						const vm = self.alternativeRecommendationVM;
						if(vm && vm.getRecommendations){
							const arrRecommendations = vm.getRecommendations();
							if(arrRecommendations && arrRecommendations.length >0){
								oRecommendation = arrRecommendations[0];//Auto Selection, picks the first recommendation!
							}
						}
					}

					let visId;
					const widgets = oRecommendation.widgets || [];
					if (widgets[0]) {
						visId = widgets[0].spec.visId;
					}

					if (!visId) {
						return;
					}

					self.changeFixedVisType(visId, !isSetByAuto);
				},
				changeFixedVisType(visId, lockType = true) {
					const selectedContent = getContentFromWidget(self.selectedWidget.getId());
					if (!selectedContent) {
						return;
					}

					if (!lockType) {
						setContentVisTypeAuto(selectedContent);
					} else {
						setContentVisTypeFixed(selectedContent, visId);
					}
				},
				syncThumbnailStore() {
					getEnv(self).getThumbnailService()
						.then((service) => {
							const store = service.getStoreAPI();
							const thumbnailIds = store.getAllLocal()
								.map(data => data.id);
							const widgetIds = self.boardModel.layout
								.listWidgets(thumbnailIds);
							_.difference(thumbnailIds, widgetIds)
								.forEach((id) => service.delete(id));
						});
				},
				syncBgThumbnailModel() {
					const { all } = self.thumbnails;
					const current = self.layoutModel.items.reduce((acc, card) => {
						acc[card.id] = true;
						return acc;
					}, {});
					// remove all items that are no longer part of the current list of cards
					const toBeRemoved = all.filter(id => current[id]);
					if (toBeRemoved.length > 0) {
						self.thumbnails.removeFromAll(toBeRemoved);
					}
				},
				navigatePreviousCard() {
					if (!self.canNavigatePrevious()) {
						return;
					}

					let id;
					if (self.isStartingPointsDisplayed()) {
						const items = self.layoutModel.items;
						if (items && items.length) {
							id = items[items.length - 1].id;
						}
					} else {
						const currentItem = self.layoutModel.findModel(self.displayedCard);
						if (currentItem) {
							id = currentItem.getPreviousSiblingId();
						}
					}

					if (id) {
						self.setDisplayedToCard(id);
						self.setFocusOnExploreContainer();
					}
				},
				navigateNextCard() {
					if (!self.canNavigateNext()) {
						return;
					}

					const currentItem = self.layoutModel.findModel(self.displayedCard);
					if (currentItem) {
						const id = currentItem.getNextSiblingId();

						if (id) {
							self.setDisplayedToCard(id);
							self.setFocusOnExploreContainer();
						} else {
							self.setDisplayedToStartingPoints();
						}
					}
				},
				setBestDisplayedToCard(cardIds) {
					if (cardIds && cardIds.length > 0) {
						const firstCardId = cardIds[0];
						if (firstCardId) {
							self.setDisplayedToCard(firstCardId);
						}
					}
				},
				setDisplayedToCard(id) {
					self.displayedCard = id;
				},
				_updateActiveDataSourceId(cardId) {
					const dataSourceId = _getDataSourceOfSelectedCard(cardId);
					if (dataSourceId) {
						self.setActiveDataSourceId(dataSourceId);
					}
				},
				_updateWidgetLayoutModel(cardId) {
					const card = self.layoutModel.findModel(cardId);
					if (card && card.items.length > 0) {
						card.items.forEach(widget => {
							const widgetLayout = self.layoutModel.findModel(widget.id);
							if (widgetLayout && widgetLayout.isStaticWidget) {
								// the card has been rendered with all data, reset the attribute isStaticWidget to false
								widgetLayout.setStaticWidget(false);
							}
						});
					}
				},
				setDisplayedToNewCard(id) {
					self.layoutController.getView(id).viewReady.then( (/*view*/) => {
						self.setDisplayedToCard(id);
					});
				},
				setDisplayedToStartingPoints() {
					self.displayedCard = STARTING_POINTS;
				},
				setIndexOfDisplayedCard(){
					self.indexOfDisplayedCard = self.displayedCardIndex;
				},
				setShowModalVisTypePicker(show) {
					if(show) {
						if (self.showModalRelatedVisPicker) {
							self.showModalRelatedVisPicker = false;
						}
						self._getAlternativeVisualizations();
					}
					self.showModalVisTypePicker = show;
				},
				setShowCompareSlideout(show) {
					if (show) {
						self._fetchCompareCardRecommendations();
					}
					self.showCompareSlideout = show;
				},
				_getAlternativeVisualizations() {
					const widgetModel = self.selectedWidgetModel;
					if (widgetModel) {
						if (!_.isEqual(widgetModel.toJSON(), self.widgetSnapshotForAlternative)) {
							self.alternativeRecommendationVM.setRecommendations([]);
							_getWidget(widgetModel.id)
								.then(widget => self.alternativeRecommendationVM.fetchRecommendations(widget, 'alternative'))
								.then(() => self.setWidgetSnapshotForAlternative());
						}
					}
				},

				_fetchCompareCardRecommendations() {
					const widgetModel = self.selectedWidgetModel;
					if (widgetModel) {
						const isDifferent = JSON.stringify(widgetModel.toJSON()) !== self.widgetSnapshotForCompareCardRecommendations;
						if (isDifferent) {
							self.compareCardSmartsRecommendationVM.setRecommendations([]);
							self.compareCardCustomRecommendationVM.setRecommendations([]);
							_getWidget(widgetModel.id)
								.then((widgetApi) => {
									const idMap = {};
									const clonedModel = widgetModel.cloneWidget(idMap);
									const widgetWrapper = {
										widgetApi,
										widgetModel: clonedModel
									};
									return Promise.all([
										self.compareCardSmartsRecommendationVM.fetchRecommendations(widgetWrapper),
										self.compareCardCustomRecommendationVM.fetchRecommendations(widgetWrapper)
									]);
								})
								.then(() => self.setWidgetSnapshotForCompareCardRecommendations());
						}
					}
				},

				_getRelatedVisualizations() {
					const widgetModel = self.selectedWidgetModel;
					if (widgetModel) {
						if (!_.isEqual(widgetModel.toJSON(), self.widgetSnapshotForRelated)) {
							self.relatedRecommendationVM.setRecommendations([]);
							_getWidget(widgetModel.id)
								.then(widget => self.relatedRecommendationVM.fetchRecommendations(widget, 'related'))
								.then(() => self.setWidgetSnapshotForRelated());
						}
					}
				},
				setShowModalRelatedVisPicker(show) {
					if(show) {
						if (self.showModalVisTypePicker) {
							self.showModalVisTypePicker = false;
						}
						self._getRelatedVisualizations();
					}
					self.showModalRelatedVisPicker = show;
				},
				setWidgetSnapshotForAlternative() {
					const widgetModel = self.selectedWidgetModel;
					self.widgetSnapshotForAlternative = widgetModel ? widgetModel.toJSON() : null;
				},
				setWidgetSnapshotForRelated() {
					const widgetModel = self.selectedWidgetModel;
					self.widgetSnapshotForRelated = widgetModel ? widgetModel.toJSON() : null;
				},
				/**
				 * Cache the last selected widget's JSON that was used to fetch compare card recommendations
				 * if the current selected widget status is the same as the cached one, no need to send request
				 * for new recommendations
				 */
				setWidgetSnapshotForCompareCardRecommendations() {
					const widgetModel = self.selectedWidgetModel;
					self.widgetSnapshotForCompareCardRecommendations = widgetModel ? widgetModel.toJSON() : null;
				},
				setLayoutController(layoutController) {
					self.layoutController = layoutController;

					self.syncCardSelection();
					self.syncWidgetSelection();
					if (self.displayedCard !== STARTING_POINTS) {
						forceAddCurrentThumbnail(self.displayedCard);
						self.layoutController.getLayoutViewWhenReady(self.displayedCard)
							.then((layoutView) => {
								setTimeout( () => {
									layoutView.triggerOnShowCard();
									layoutView.__triggeredShow  && layoutView.__triggeredShow(); //only used for unit testing
								}, 0);
							});
					}

					self._mobxDisposeEvents.push(
						mobx.reaction(() => {
							return self.displayedCard;
						}, displayedCard => {
							self.selectionSync.setDisplayedCard(displayedCard);
							//setTimeout( () => {
							self.deselectAllWidgets();
							self.syncCardSelection();
							self.syncWidgetSelection();
							//}, 1);
							//TODO: try async ?

							if (self.isCardNew(displayedCard)) {
								self.markCardAsNew(displayedCard, false);
							}

							self.notifyTabChanged(self.displayedCard);

							let promise;
							// only one card can be visible at a time
							if (displayedCard !== STARTING_POINTS) {
								forceAddCurrentThumbnail(self.displayedCard);
								self._updateActiveDataSourceId(self.displayedCard);
								self._updateWidgetLayoutModel(self.displayedCard);
								//Normally, triggerWidgetRender is sufficient to ensure a card and its widgets are rendered, however;
								//when moving to a new card (tab) which has previously been rendered as a thumbnail, we need to ensure
								// the render occurs to add interactive features to the rendering.
								promise = self.layoutController.getLayoutViewWhenReady(displayedCard)
									.then((layoutView) => layoutView.triggerOnShowCard());
							} else {
								// update data source of the explore view to the last active data source
								const activeDataSourceId = getEnv(self).dashboardApi.getActiveDataSourceId();
								if (activeDataSourceId && activeDataSourceId !== self.activeDataSourceId) {
									promise = self.setActiveDataSourceId(activeDataSourceId);
								} else {
									promise = Promise.resolve();
								}
							}

							return promise;
						})
					);
				},

				handleDisplayedCardFeatures(displayedCard) {
					const cardModel = self.layoutModel.findModel(displayedCard);
					if (cardModel) {
						if (cardModel.isCompareEnabled) {
							return self.activateCanvasFeature(FEATURES.CANVAS.FILTER_DOCK);
						} else {
							return self.deactivateCanvasFeature(FEATURES.CANVAS.FILTER_DOCK);
						}
					} else {
						return Promise.resolve();
					}
				},

				setBoardModel(boardModel) {
					self.boardModel = boardModel;
				},
				setActiveDataSourceId(dataSourceId) {
					self.activeDataSourceId = dataSourceId;
					const currentDataSourceId = getEnv(self).dashboardApi.getActiveDataSourceId();
					if (dataSourceId !== currentDataSourceId) {
						getEnv(self).dashboardApi.setActiveDataSourceId(dataSourceId);
						self._setActiveDataSourceModule();
					}
				},
				_setActiveDataSourceModule() {
					let promise;
					const dataSourceId = self.activeDataSourceId;
					if(dataSourceId) {
						promise = getEnv(self).dashboardApi.getFeature('dataSources.deprecated').getModule(dataSourceId)
							.then(module => {
								const cols = module.getMetadataColumns(() => true, true);
								if(!cols || !cols.length) {
									throw new Error('Data source has no metadata columns');
								}
								return module;
							});
						self.activeDataSourceModule = fromPromise(
							promise
						);
					}
					return promise || Promise.resolve();
				},
				setVisTypes(visTypes) {
					self.visTypes = visTypes;
				},
				setLayoutModel(layoutModel) {
					self.layoutModel = layoutModel;
					self.setCardActions(new CardActions({ exploreVM: self, layoutModel }));
					self.prevItemCount = self.layoutModel.items.length;

					self._mobxDisposeEvents.push(
						mobx.reaction(() => {
							return self.notification.isListOpen;
						}, () => {
							self.prevItemCount = self.layoutModel.items.length;
							self.syncThumbnailStore();
						}),
						mobx.reaction(() => {
							return self.layoutModel.items.length;
						}, () => {
							if(self.displayedCard && !self.isStartingPointsDisplayed()) {
								const card = self.layoutModel.items.find((card) => card.id === self.displayedCard);
								if(!card) {
									if (self.layoutModel.items.length === 0) {
										self.setDisplayedToStartingPoints();
									} else {
										let nextIndex = self.indexOfDisplayedCard - 1;
										//Clamp value
										nextIndex = Math.max(0, Math.min(nextIndex, self.layoutModel.items.length - 1));
										const layoutItem = self.layoutModel.items[nextIndex];
										if (layoutItem) {
											self.setDisplayedToCard(layoutItem.id);
										}
									}
								}
							}

							//Only activate the notification is the side panel is not shown, and the amount of cards has increased since the user last had the side panel open
							if(!self.notification.isListOpen && self.layoutModel.items.length > self.prevItemCount) {
								self.notification.setActive(true);
							} else {
								self.notification.setActive(false);
							}
							self.syncThumbnailStore();
							self.syncBgThumbnailModel();
						}),

						//Make indexOfDisplayedCard is consistent with displayedCard
						mobx.reaction(() => {
							return self.displayedCard;
						}, () => {
							self.setIndexOfDisplayedCard();
						}),

						mobx.reaction(() => {
							const cardModel = self.layoutModel && self.layoutModel.findModel(self.displayedCard);

							const fetchables = [];
							if (cardModel && cardModel.items) {
								cardModel.items.forEach(item => {
									if (item.type === 'widget') {
										const widgetModel = self.layoutModel.findModel(item.id);
										if (widgetModel && widgetModel.nlt) {
											fetchables.push(widgetModel.nlt.shouldFetch);
										}
									}
								});
							}

							return fetchables.some(item => item);
						}, changed => {
							if (changed) {
								self.fetchNLT();
							}
						}, { delay:300 }),

						// Background thumbnail generation
						// react to all changes to the `current` list in the
						// thumbnails model
						// This logic is responsible for the transition of
						// card ids from `current` to `done`
						mobx.reaction(() => {
							return self.thumbnails.current
								.map((cardId) => {
									const cardModel = self.layoutModel.findModel(cardId);
									const widgets = (cardModel && cardModel.items) || [];

									const allWidgetsHaveThumbnails = widgets
										.filter(item => item.type === 'widget')
										.every(item => {
											const widgetModel = self.layoutModel.findModel(item.id);
											if (!widgetModel) {
												return false;
											}

											const imageCapture = widgetModel.imageCapture;
											return imageCapture && imageCapture.isThumbnailReady();
										});
									return allWidgetsHaveThumbnails ? cardId : undefined;
								}).filter(value => !!value);
						}, (cardIds) => {
							if (!cardIds) {
								// it should never happen, but we should protect
								// against throwing errors in case the reaction had an error
								return;
							}
							cardIds.forEach((cardId) => {
								// move card from `current` state to `done`
								self.thumbnails.removeFromCurrent(cardId);
								self.thumbnails.addToDone(cardId);
							});
						}, {
							fireImmediately: true
						})
					);
				},
				setCardActions(cardActions) {
					self.cardActions = cardActions;
					self.toolbar.setCardActions(cardActions);
				},
				toggleCanvasSidePanel() {
					self.showCanvasSidePanel = !self.showCanvasSidePanel;
					if(!self.showCanvasSidePanel){
						self.canvasTabs.tab = '';
					}
				},
				setToolBarVisTypePicker(value, widgetInstance) {
					self.widgetInstance = widgetInstance;
					self.toolbarVisPicker = value;
				},
				resetThumbnailsQueue() {
					if (self._isAlive(self.thumbnails)) {
						self.thumbnails.resetQueue();
					}
				},
				markCardAsNew(cardId, state = true) {
					const card = self.layoutModel.findModel(cardId);
					if (card) {
						card.setIsNew(state);
					}
				}
			}
		};
	});



// WEBPACK FOOTER //
// ./src/js/layout/explorecontainer/ExploreViewModel.js